import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Undo2 } from 'lucide-react';

import CardsGroup from '../../components/dynamicFieldCards/CardsGroup';
import NestedCardsGroup from '../../components/dynamicFieldCards/nested/CardsGroup';
import ArrayCardsGroup from '../../components/dynamicFieldCards/arrayOfTextInputs/CardsGroup';
import CustomLabels from '../../components/CustomLabels';
import { showToast } from '../../components/ToastConfig';
import { useAuth } from '../../contexts/AuthContext';

import { STATUS_MESSAGES } from '../../utils/api-handlers/company-form';

import NewCompanyService from '../../services/company-service';

// Step1Form with basic validation
const Step1Form = ({ register, errors }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4 gap-4">
    <div className="space-y-1">
      <label className="block text-sm font-medium">name
        <span className="text-red-500 ml-1">*</span>
      </label>
      <input
        {...register('name', { 
          required: '公司名稱為必填欄位'
        })}
        className={`w-full p-2 border rounded ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.name && (
        <p className="text-red-500 text-sm">{errors.name.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">legal_name
        <span className="text-red-500 ml-1">*</span>
      </label>
      <input
        {...register('legal_name', { 
          required: '公司法律名稱為必填欄位'
        })}
        className={`w-full p-2 border rounded ${errors.legal_name ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.legal_name && (
        <p className="text-red-500 text-sm">{errors.legal_name.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">contact_email</label>
      <input
        {...register('contact_email', {
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: '請輸入有效的 Email 格式'
          }
        })}
        className={`w-full p-2 border rounded ${errors.contact_email ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.contact_email && (
        <p className="text-red-500 text-sm">{errors.contact_email.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">about</label>
      <input
        {...register('about')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">full_description</label>
      <input
        {...register('full_description')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">address</label>
      <input
        {...register('address')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">contact_phone</label>
      <input
        {...register('contact_phone')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">website</label>
      <input
        {...register('website')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
  </div>
);

const Step2Form = ({ register, errors }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4 gap-4">
    <div className="space-y-1">
      <label className="block text-sm font-medium">stock_symbol</label>
      <input
        {...register('stock_symbol')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">company_type</label>
      <input
        {...register('company_type')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">operating_status</label>
      <input
        {...register('operating_status')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">ipo_status</label>
      <input
        {...register('ipo_status')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">country_code</label>
      <input
        {...register('country_code')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">type</label>
      <input
        {...register('type')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">cb_rank</label>
      <input
        {...register('cb_rank', {
          pattern: {
            value: /^\d*$/,
            message: '請輸入有效的數字'
          }
        })}
        className="w-full p-2 border rounded border-gray-300"
      />
      {errors.cb_rank && (
        <p className="text-red-500 text-sm">{errors.cb_rank.message}</p>
      )}
    </div>
  </div>
);

const Step3Form = ({ register, errors }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4 gap-4">
    {/* All number fields */}
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_employees</label>
      <input
        {...register('num_employees')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_acquisitions</label>
      <input
        {...register('num_acquisitions', {
          pattern: {
            value: /^\d*$/,
            message: '請輸入有效的數字'
          }
        })}
        className="w-full p-2 border rounded border-gray-300"
      />
      {errors.num_acquisitions && (
        <p className="text-red-500 text-sm">{errors.num_acquisitions.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_investments</label>
      <input
        {...register('num_investments', {
          pattern: {
            value: /^\d*$/,
            message: '請輸入有效的數字'
          }
        })}
        className="w-full p-2 border rounded border-gray-300"
      />
      {errors.num_investments && (
        <p className="text-red-500 text-sm">{errors.num_investments.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_exits</label>
      <input
        {...register('num_exits', {
          pattern: {
            value: /^$|^\d*$/,
            message: '請輸入有效的數字'
          }
        })}
        className="w-full p-2 border rounded border-gray-300"
      />
      {errors.num_exits && (
        <p className="text-red-500 text-sm">{errors.num_exits.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">founded_date</label>
      <input
        {...register('founded_date', {
          pattern: {
            value: /^$|^\d+$|^\d{4}-\d{2}-\d{2}$/,
            message: '請輸入純數字或使用 YYYY-MM-DD 格式'
          }
        })}
        className="w-full p-2 border rounded border-gray-300"
      />
      {errors.founded_date && (
        <p className="text-red-500 text-sm">{errors.founded_date.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">funding_rounds.last_funding_type</label>
      <input
        {...register('funding_rounds.last_funding_type')}
        className="w-full p-2 border rounded border-gray-300"
      />
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">ipo_fields.date</label>
      <input
        {...register('ipo_fields.date', {
          pattern: {
            value: /^$|^\d+$|^\d{4}-\d{2}-\d{2}$/,
            message: '請輸入純數字或使用 YYYY-MM-DD 格式'
          }
        })}
        className="w-full p-2 border rounded border-gray-300"
      />
      {errors?.ipo_fields?.date && (
        <p className="text-red-500 text-sm">{errors?.ipo_fields?.date.message}</p>
      )}
    </div>
  </div>
);

const Step4Form = ({ register, errors, setValue, watch }) => {
  const locationFields = [
    { name: 'name', label: 'name' },
    { name: 'permalink', label: 'permalink' },
  ];

  const industryFields = [
    { name: 'id', label: 'id' },
    { name: 'value', label: 'value' }
  ];

  const founderFields = [
    { name: 'value', label: 'value' }
  ];

  const headerQuarterFields = [
    { name: 'id', label: 'id' },
    { name: 'value', label: 'value' }
  ];

  const investorFields = [
    { name: 'id', label: 'id' },
    { name: 'type', label: 'type' },
    { name: 'value', label: 'value' },
    { name: 'lead_investor', label: 'lead_investor', type: 'boolean' }
  ];
  
  return (  
    <div className="space-y-8">
      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="location" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="location"
          fields={locationFields}
          fieldTitle="location"
        />
      </div>
      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="industries" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="industries"
          fields={industryFields}
          fieldTitle="industries"
        />
      </div>
      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="founders" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="founders"
          fields={founderFields}
          fieldTitle="founders"
        />
      </div>
      <div>
        <CustomLabels texts="headquarters_regions" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="headquarters_regions"
          fields={headerQuarterFields}
          fieldTitle="headquarters_regions"
        />
      </div>
      <div>
        <CustomLabels texts="investors" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="investors"
          fields={investorFields}
          fieldTitle="investors"
        />
      </div>
    </div>
  );
};

const Step5Form = ({ register, errors, setValue, watch, mode }) => {
  const fundingRoundFields = [
    { 
      name: 'announced_on',
      label: 'announced_on',
      type: 'text',
      pattern: {
        value: /^$|^\d+$|^\d{4}-\d{2}-\d{2}$/,
        message: '請輸入純數字或使用 YYYY-MM-DD 格式'
      }
    },
    { 
      name: 'lead_investors', 
      label: 'lead_investors', 
      type: 'nested-array',
      fields: [
        { name: 'image', label: 'image', type: 'text' },
        { name: 'name', label: 'name', type: 'text' },
        { name: 'permalink', label: 'permalink', type: 'text' }
      ]
    },
    { name: 'id', label: 'id', type: 'text' },
    { name: 'image_id', label: 'image_id', type: 'text' },
    { name: 'title', label: 'title', type: 'text' },
    { name: 'uuid', label: 'uuid', type: 'text' },
    { 
      name: 'money_raised',
      label: '募資金額',
      type: 'object',
      fields: [
        { name: 'value_usd', label: 'value_usd' },
        { name: 'currency', label: 'currency' },
        { name: 'value', label: 'value' }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="funding_rounds_list" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="funding_rounds_list"
          fields={fundingRoundFields}
          fieldTitle="funding_rounds"
          mode={mode}
        />
      </div>
    </div>
  );
};

const Step6Form = ({ register, errors, setValue, watch, mode }) => {
  const investmentFields = [
    { 
      name: 'announced_on',
      label: 'announced_on',
      type: 'text',
      pattern: {
        value: /^$|^\d+$|^\d{4}-\d{2}-\d{2}$/,
        message: '請輸入純數字或使用 YYYY-MM-DD 格式'
      }
    },
    { 
      name: 'organization', 
      label: '組織', 
      type: 'object',
      fields: [
        { name: 'id', label: 'id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'type', label: 'type', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'funding_round', 
      label: '募資輪次', 
      type: 'object',
      fields: [
        { name: 'id', label: 'id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'type', label: 'type', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="investments" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="investments"
          fields={investmentFields}
          fieldTitle="investments"
          mode={mode}
        />
      </div>
    </div>
  );
};

const Step7Form = ({ register, errors, setValue, watch, mode }) => {
  const diversityInvestmentFields = [
    { 
      name: 'announced_on',
      label: 'announced_on',
      type: 'text',
      pattern: {
        value: /^$|^\d+$|^\d{4}-\d{2}-\d{2}$/,
        message: '請輸入純數字或使用 YYYY-MM-DD 格式'
      }
    },
    { 
      name: 'organization', 
      label: '組織', 
      type: 'object',
      fields: [
        { name: 'id', label: 'id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'type', label: 'type', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'funding_round', 
      label: '募資輪次', 
      type: 'object',
      fields: [
        { name: 'id', label: 'id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'type', label: 'type', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { name: 'id', label: 'id', type: 'text' },
    { name: 'type', label: 'type', type: 'text' },
    { name: 'value', label: 'value', type: 'text' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="diversity_investments" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="diversity_investments"
          fields={diversityInvestmentFields}
          fieldTitle="diversity_investments"
          mode={mode}
        />
      </div>
    </div>
  );
};

const Step8Form = ({ register, errors, setValue, watch }) => {
  const acquisitionFields = [
    { 
      name: 'identifier', 
      label: 'identifier',
      type: 'object',
      fields: [
        { name: 'entity_def_id', label: 'entity_def_id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'permalink', label: 'permalink', type: 'text' },
        { name: 'uuid', label: 'uuid', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'price', 
      label: 'price',
      type: 'object',
      fields: [
        { name: 'value_usd', label: 'value_usd', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'announced_on', 
      label: 'announced_on',
      type: 'object',
      fields: [
        { name: 'value', label: 'value', type: 'text' }
      ],
      pattern: {
        value: /^$|^\d+$|^\d{4}-\d{2}-\d{2}$/,
        message: '請輸入純數字或使用 YYYY-MM-DD 格式'
      }
    },
    { 
      name: 'acquiree_identifier', 
      label: 'acquiree_identifier',
      type: 'object',
      fields: [
        { name: 'role', label: 'role', type: 'text' },
        { name: 'entity_def_id', label: 'entity_def_id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'permalink', label: 'permalink', type: 'text' },
        { name: 'uuid', label: 'uuid', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="acquisitions" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="acquisitions"
          fields={acquisitionFields}
          fieldTitle="acquisitions"
        />
      </div>
    </div>
  );
};

const Step9Form = ({ register, errors, setValue, watch }) => {
  const exitFields = [
    { name: 'image', label: 'image' },
    { name: 'link', label: 'link' },
    { name: 'description', label: 'description' },
    { name: 'value', label: 'value' }
  ];

  const featuredListFields = [
    { name: 'title', label: 'title' },
    { name: 'org_num', label: 'org_num' },
    { name: 'org_num_investors', label: 'org_num_investors' },
    { 
      name: 'org_funding_total', 
      label: 'org_funding_total',
      type: 'object',
      fields: [
        { name: 'currency', label: 'currency' },
        { name: 'value', label: 'value' },
        { name: 'value_usd', label: 'value_usd' }
      ]
    }
  ];

  const contactFields = [
    { name: 'name', label: 'name' },
    { name: 'job_title', label: 'job_title' },
    { name: 'linkedin_id', label: 'linkedin_id' },
    { name: 'departments', label: 'departments', type: 'array' },
    { name: 'levels', label: 'levels', type: 'array' }
  ];

  const financialsFields = [
    { name: 'num_lead_investors', label: 'num_lead_investors' },
    { name: 'num_investors', label: 'num_investors' },
    { name: 'num_funding_rounds', label: 'num_funding_rounds' }
  ];

  return (
    <div className="space-y-8">
      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="exits" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="exits"
          fields={exitFields}
          fieldTitle="exits"
        />
      </div>

      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="featured_list" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="featured_list"
          fields={featuredListFields}
          fieldTitle="featured_list"
        />
      </div>

      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="contacts" />
        <ArrayCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="contacts"
          fields={contactFields}
          fieldTitle="contacts"
        />
      </div>
      
      <div>
        <CustomLabels texts="financials_highlights" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="financials_highlights"
          fields={financialsFields}
          fieldTitle="financials_highlights"
          userCanAdd={false}
        />
      </div>
    </div>
  );
};

const CompanyForm = () => {
  const Navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [mode, setMode] = useState('edit');
  const [companyData, setCompanyData] = useState(null);
  const { uuid } = useParams();
  const { logout } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    watch,
    setValue,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: mode === "edit" ? {} : {
      // "location": [
      //   {
      //     name: '',
      //     permalink: ''
      //   }
      // ],
      // "industries": [
      //   {
      //     id: '',
      //     value: ''
      //   }
      // ],
      // "founders": [
      //   {
      //     value: ''
      //   }
      // ],
      // "headquarters_regions": [
      //   {
      //     id: "",
      //     value: ""
      //   }
      // ],
      // "funding_rounds_list": [
      //   {
      //     announced_on: '',
      //     lead_investors: [
      //       {
      //         image: '',
      //         name: '',
      //         permalink: ''
      //       }
      //     ],
      //     id: '',
      //     image_id: '',
      //     title: '',
      //     uuid: '',
      //     money_raised: {
      //       value_usd: '',
      //       currency: '',
      //       value: ''
      //     }
      //   }
      // ]
    }
  });

  const handleNext = async () => {
    // 檢查當前表單的所有錯誤
    const currentErrors = Object.keys(errors);
    
    if (currentErrors.length > 0) {
        showToast.error('請修正錯誤後再繼續');
        return;
    }

    // 獲取當前步驟的表單數據
    const currentFormData = watch();

    // 根據當前步驟執行特定的驗證
    let isValid = true;
    switch (currentStep) {
        case 1:
            isValid = await trigger(['name', 'legal_name', 'contact_email']);
            break;
        case 2:
            isValid = await trigger(['cb_rank']);
            break;
        case 3:
            isValid = await trigger([
                'num_acquisitions',
                'num_investments',
                'num_exits',
                'founded_date',
                'ipo_fields.date'
            ]);
            break;
        case 5:
            // 驗證 funding_rounds_list 中所有項目的 announced_on
            // if (currentFormData.funding_rounds_list?.length > 0) {
            //     const fundingPromises = currentFormData.funding_rounds_list.map((_, index) => 
            //         trigger(`funding_rounds_list.${index}.announced_on`)
            //     );
            //     const results = await Promise.all(fundingPromises);
            //     isValid = results.every(result => result === true);
            // }
            // break;
        case 6:
            // 驗證 investments 中所有項目的 announced_on
            // if (currentFormData.investments?.length > 0) {
            //     const investmentPromises = currentFormData.investments.map((_, index) => 
            //         trigger(`investments.${index}.announced_on`)
            //     );
            //     const results = await Promise.all(investmentPromises);
            //     isValid = results.every(result => result === true);
            // }
            // break;
        case 7:
            // 驗證 diversity_investments 中所有項目的 announced_on
            // if (currentFormData.diversity_investments?.length > 0) {
            //     const diversityPromises = currentFormData.diversity_investments.map((_, index) => 
            //         trigger(`diversity_investments.${index}.announced_on`)
            //     );
            //     const results = await Promise.all(diversityPromises);
            //     isValid = results.every(result => result === true);
            // }
            // break;
        case 8:
            // 驗證 acquisitions 中所有項目的 announced_on
            // if (currentFormData.acquisitions?.length > 0) {
            //     const acquisitionPromises = currentFormData.acquisitions.map((_, index) => 
            //         trigger(`acquisitions.${index}.announced_on.value`)
            //     );
            //     const results = await Promise.all(acquisitionPromises);
            //     isValid = results.every(result => result === true);
            // }
            // break;
    }

    if (isValid) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo(0, 0);
    } else {
        showToast.error('請確認所有欄位填寫正確');
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const onSubmit = async (data) => {
    // // 檢查當前表單的所有錯誤
    // const currentErrors = Object.keys(errors);

    // if (currentErrors.length > 0) {
    //     showToast.error('請修正錯誤後再提交');
    //     return;
    // }

    // console.log("currentErrors: ", currentErrors);

    // 檢查 email 格式
    if (data.contact_email) {  // 如果有填寫 email
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(data.contact_email)) {
          showToast.error('請輸入有效的 Email 格式');
          return;
      }
    }

    if (data.founded_date) {
      if (!/^$|^\d+$|^\d{4}-\d{2}-\d{2}$/.test(data.founded_date)) {
        showToast.error("step3 founded_date 格式錯誤");
        return;
      }
    }
    
    if (data.ipo_fields?.date) {
      if (!/^$|^\d+$|^\d{4}-\d{2}-\d{2}$/.test(data.ipo_fields.date)) {
        showToast.error("step3 ipo_fields.date 格式錯誤");
        return;
      }
    }
    
    let hasValidationError = false;

    let { num_acquisitions, num_investments, num_exits, cb_rank, founded_date, ipo_fields, investors, funding_rounds_list, investments, diversity_investments, acquisitions, financials_highlights, ...rest } = data;
    if (num_acquisitions === "") {
      num_acquisitions = 0;
    }
    if (num_investments === "") {
      num_investments = 0;
    }
    if (num_exits === "") {
      num_exits = 0;
    }

    if (cb_rank === "") {
      cb_rank = 0;
    }
    if (founded_date === "") {
      founded_date = "0";
    }
    if (ipo_fields?.date === "") {
      ipo_fields.date = "0";
    }

    investors = investors?.map((investor) => {
      if (investor?.lead_investor === "") {
        return {
          ...investor,
          lead_investor: false
        };
      } else {
        return investor;
      }
    });

    // 處理資金輪次列表
    funding_rounds_list = funding_rounds_list?.map((round) => {
      // 如果輪次資料不存在，直接返回
      if (!round) return round;

      // 創建新物件以避免直接修改原始資料
      let processedRound = { ...round };

      // 處理募資金額相關資料
      if (processedRound.money_raised) {
        processedRound.money_raised = {
          ...processedRound.money_raised,
          // 將空字串轉為 0，其他情況轉為數字型態
          value_usd: processedRound.money_raised.value_usd === "" ? 0 : Number(processedRound.money_raised.value_usd),
          value: processedRound.money_raised.value === "" ? 0 : Number(processedRound.money_raised.value)
        };
      }

      // 處理宣布日期
      if (processedRound.announced_on !== undefined) {
        // 將空字串轉為 null，保留其他有效日期
        if (processedRound.announced_on === "") {
          processedRound.announced_on = null;
        } else if (processedRound.announced_on === null) {
          processedRound.announced_on = null;
        } else if (!/^$|^\d+$|^\d{4}-\d{2}-\d{2}$/.test(processedRound.announced_on)) {
          showToast.error("step5 announced_on 格式錯誤");
          hasValidationError = true;
          return round; // 返回原始數據，保持表單狀態
        }
      }

      return processedRound;
    });

    // 如果有驗證錯誤，阻止表單提交
    if (hasValidationError) {
      return;
    }

    investments = investments?.map((investment) => {
      if (!investment) return investment;
      const processedInvestment = { ...investment };
      if (processedInvestment.announced_on !== undefined) {
        if (processedInvestment.announced_on === "") {
          processedInvestment.announced_on = null;
        } else if (processedInvestment.announced_on === null) {
          processedInvestment.announced_on = null;
        } else if (!/^$|^\d+$|^\d{4}-\d{2}-\d{2}$/.test(processedInvestment.announced_on)) {
          showToast.error("step6 announced_on 格式錯誤");
          hasValidationError = true;
          return investment; // 返回原始數據，保持表單狀態
        }
      }
      return processedInvestment;
    });
    
    // 如果有驗證錯誤，阻止表單提交
    if (hasValidationError) {
      return;
    }
    
    diversity_investments = diversity_investments?.map((diversityInvestment) => {
      if (!diversityInvestment) return diversityInvestment;
      const processedDiversityInvestment = { ...diversityInvestment };
      if (processedDiversityInvestment.announced_on !== undefined) {
        if (processedDiversityInvestment.announced_on === "") {
          processedDiversityInvestment.announced_on = null;
        } else if (processedDiversityInvestment.announced_on === null) {
          processedDiversityInvestment.announced_on = null;
        } else if (!/^$|^\d+$|^\d{4}-\d{2}-\d{2}$/.test(processedDiversityInvestment.announced_on)) {
          showToast.error("step7 announced_on 格式錯誤");
          hasValidationError = true;
          return diversityInvestment; // 返回原始數據，保持表單狀態
        }
      }
      return processedDiversityInvestment;
    });
    
    // 如果有驗證錯誤，阻止表單提交
    if (hasValidationError) {
      return;
    }

    acquisitions = acquisitions?.map((acquisition) => {
      let processedAcquisition = { ...acquisition };
    
      if (acquisition?.price) {
        processedAcquisition.price = {
          ...acquisition.price,
          value_usd: acquisition.price.value_usd === "" ? 0 : acquisition.price.value_usd,
          value: acquisition.price.value === "" ? 0 : acquisition.price.value
        };
      }
    
      if (acquisition?.announced_on) {
        if (acquisition.announced_on.value === "") {
          processedAcquisition.announced_on = {
            value: null
          };
        } else if (processedAcquisition.announced_on.value === null) {
          processedAcquisition.announced_on.value = null;
        } else if (!/^$|^\d+$|^\d{4}-\d{2}-\d{2}$/.test(acquisition.announced_on.value)) {
          showToast.error("step8 announced_on 格式錯誤");
          hasValidationError = true;
          return acquisition; // 返回原始數據，保持表單狀態
        } else {
          processedAcquisition.announced_on = {
            value: acquisition.announced_on.value
          };
        }
      }
    
      return processedAcquisition;
    });
    
    // 如果有驗證錯誤，阻止表單提交
    if (hasValidationError) {
      return;
    }

    // 處理 featured_list 數字欄位
    const featured_list = data.featured_list?.map(item => {
        let processedItem = { ...item };
        
        // 處理直接屬性
        processedItem.org_num = item.org_num === "" ? 0 : Number(item.org_num);
        processedItem.org_num_investors = item.org_num_investors === "" ? 0 : Number(item.org_num_investors);
        
        // 處理巢狀物件
        if (item.org_funding_total) {
            processedItem.org_funding_total = {
                ...item.org_funding_total,
                value_usd: item.org_funding_total.value_usd === "" ? 0 : Number(item.org_funding_total.value_usd),
                value: item.org_funding_total.value === "" ? 0 : Number(item.org_funding_total.value)
            };
        }
        
        return processedItem;
    });

    // 處理 financials_highlights：只取第一項並轉換成物件格式
    const financials = financials_highlights?.[0] ? {
      num_lead_investors: financials_highlights[0].num_lead_investors === "" ? 0 : Number(financials_highlights[0].num_lead_investors),
      num_investors: financials_highlights[0].num_investors === "" ? 0 : Number(financials_highlights[0].num_investors),
      num_funding_rounds: financials_highlights[0].num_funding_rounds === "" ? 0 : Number(financials_highlights[0].num_funding_rounds)
    } : null;

    const processedData = { ...rest, num_acquisitions, num_investments, num_exits, cb_rank, founded_date, ipo_fields, investors, funding_rounds_list, investments, diversity_investments, acquisitions, ipo_fields, featured_list, financials_highlights: financials };

    try {
      let res;
      if (mode === "edit") {
        res = await NewCompanyService.update(uuid, processedData);
        showToast.success('公司資料更新成功');
      } else {
        res = await NewCompanyService.create(processedData);
        showToast.success('公司建立成功');
      }

      setTimeout(() => {
        Navigate(`/company`);
      }, 1000);
    } catch (error) {
      console.error('表單提交失敗:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else if (error?.response?.status === 400) {
        showToast.error(STATUS_MESSAGES[error?.response?.data?.errorCode] || STATUS_MESSAGES['default']);
      } else {
        showToast.error(STATUS_MESSAGES[error?.response?.status] || '提交失敗，請稍後再試');
      }
    }
  };

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!uuid || uuid.trim() === "new") {
        setMode('create');
        return;
      }
      try {
        setMode('edit');
        const data = await NewCompanyService.getDetails(uuid);

        const modifiedData = {
          ...data?.data,
          financials_highlights: [data?.data?.financials_highlights]
        };

        setCompanyData(modifiedData);
        Object.keys(modifiedData).forEach(key => {
          setValue(key, modifiedData[key]);
        });

        // setCompanyData(data?.data);
        // Object.keys(data?.data).forEach(key => {
        //   setValue(key, data?.data[key]);
        // });
      } catch (error) {
        console.error('Failed to fetch company data:', error?.response?.data);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          // showToast.error('無權訪問此頁面');
          // return;
          logout();
        }
        if (error?.response?.status === 400 && error?.response?.data?.errorCode === "2000") {
          Navigate(`/company`);
          return;
        }
        if (error?.response?.status === 400) {
          showToast.error(STATUS_MESSAGES[error?.response?.data?.errorCode] || STATUS_MESSAGES['default']);
          return;
        } else {
          showToast.error(STATUS_MESSAGES[error?.response?.status] || '獲取公司資料失敗，請稍後再試');
        }
      }
    }

    fetchCompanyData();
  }, [uuid, setValue, Navigate]);

  const formValues = watch();

  return (
    <div className="container mx-auto p-6">
      <Link to={`/company`} className="text-blue-500 hover:text-blue-600 group flex items-center mb-5 w-fit p-3 -ml-3">
        <Undo2 size={36} className='text-blue-500 group-hover:text-blue-600 mr-1' />  
        <span className='font-bold'>返回</span>
      </Link>
      <div className="mb-8">
        <div className="flex justify-between items-center relative">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((step) => (
            <div
              key={step}
              className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${
                currentStep >= step ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
              onClick={() => setCurrentStep(step)}
            >
              {step}
            </div>
          ))}
          <div className='absolute top-1/2 -translate-y-full w-full h-[1px] bg-gray-200 -z-10'></div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {currentStep === 1 && <Step1Form register={register} errors={errors} />}
        {currentStep === 2 && <Step2Form register={register} errors={errors} />}
        {currentStep === 3 && <Step3Form register={register} errors={errors} />}
        {currentStep === 4 && <Step4Form register={register} errors={errors} setValue={setValue} watch={watch} />}
        {currentStep === 5 && <Step5Form register={register} errors={errors} setValue={setValue} watch={watch} mode={mode} />}
        {currentStep === 6 && <Step6Form register={register} errors={errors} setValue={setValue} watch={watch} mode={mode} />}
        {currentStep === 7 && <Step7Form register={register} errors={errors} setValue={setValue} watch={watch} mode={mode} />}
        {currentStep === 8 && <Step8Form register={register} errors={errors} setValue={setValue} watch={watch} />}
        {currentStep === 9 && <Step9Form register={register} errors={errors} setValue={setValue} watch={watch} />}

        <div className={`flex ${currentStep === 1 ? 'justify-end' : 'justify-between'} mt-8`}>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrev}
              className="px-4 py-2 border border-gray-600 text-gray-600 rounded hover:bg-gray-50"
            >
              上一頁
            </button>
          )}
          
          <div className='flex items-center'>
            {currentStep < 9 && (
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
              >
                繼續填寫
              </button>
            )}
          
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ml-4"
              disabled={isSubmitting}
            >
              {isSubmitting ? "提交中..." : "提交表單"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;